import React, { useState } from 'react';
import {
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ResponsiveSheet } from '../ui/responsive-sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { getDeviceUIModel } from '../../lib/deviceUiModel';
import { cn } from '../../lib/utils';
import DeviceEditor from './DeviceEditor';
import { Lock, Unlock, AlertTriangle, ChevronDown, ChevronRight, Minus, Plus, MapPin } from 'lucide-react';

const DeviceDetailsSheet = ({ deviceId, devices, open, onClose, onControl, onDelete, loading }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAllControls, setShowAllControls] = useState(false);
  const [optimisticControlValues, setOptimisticControlValues] = useState({});


  React.useEffect(() => {
    if (!open) {
      setOptimisticControlValues({});
    }
  }, [open]);

  const currentDevice = devices?.find(d => d.id === deviceId);

  // The overlay only bridges the gap until the hub reports. Once the real
  // value catches up with what we showed, drop the override so a later
  // revert (command failed) is not hidden behind a stale optimistic value.
  React.useEffect(() => {
    if (!currentDevice?.controls) return;
    setOptimisticControlValues(prev => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      for (const key of keys) {
        const control = currentDevice.controls.find(c => c.property === key);
        if (control && control.value === prev[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [currentDevice?.lastUpdated, currentDevice?.controls]);

  const handleControlChange = (property, value) => {
    if (onControl && currentDevice) {
      const isCurtain = uiModel.kind === 'curtain';
      const newOptimisticValues = {
        ...optimisticControlValues,
        [property]: value
      };

      if (isCurtain && (value === 'OPEN' || value === 'CLOSE')) {
        newOptimisticValues[property] = 'MOVING';
      }

      setOptimisticControlValues(newOptimisticValues);
      onControl(currentDevice.id, property, value, currentDevice.parentId);

      if (isCurtain && (value === 'OPEN' || value === 'CLOSE')) {
        setTimeout(() => {
          setOptimisticControlValues(prev => ({
            ...prev,
            [property]: value
          }));
        }, 2000);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (onDelete && currentDevice) {
      await onDelete(currentDevice.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  if (!currentDevice) return null;

  const uiModel = getDeviceUIModel(currentDevice);
  const IconComponent = uiModel.icon;

  const deviceTitle = currentDevice.name || currentDevice.friendly_name || 'Unnamed Device';

  const formatWords = (s) =>
    s.split(/[\s_-]+/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const locationText =
    currentDevice.location && currentDevice.location.trim() && currentDevice.location.toLowerCase() !== 'unknown'
      ? formatWords(currentDevice.location)
      : '';

  // Lights/switches get a big tappable icon-circle hero (matches the mobile app)
  // instead of a floating switch.
  const heroBinaryControl = (uiModel.kind === 'light' || uiModel.kind === 'switch')
    ? (currentDevice.controls || []).find(c => c.valueType === 'binary')
    : null;

  // De-dup by property first — some converters (e.g. the rotary AC) expose the
  // same control twice, and th4 may not have the backend de-dup yet.
  const dedupedControls = [];
  const seenControlProps = new Set();
  (currentDevice.controls || []).forEach((c) => {
    if (c.property && !seenControlProps.has(c.property)) {
      seenControlProps.add(c.property);
      dedupedControls.push(c);
    }
  });

  // Keep out of the generic "Controls" rows only the controls the hero actually
  // renders. (The old slice(1) assumed a positional hero and wrongly hid a
  // sensor's first setting, since sensors have no hero.)
  const heroConsumed = new Set();
  if (heroBinaryControl) heroConsumed.add(heroBinaryControl.property);
  if (uiModel.kind === 'thermostat') {
    const sp = dedupedControls.find(c => c.property?.includes('current_heating_setpoint'));
    if (sp) heroConsumed.add(sp.property);
    const pw = dedupedControls.find(c => c.property === 'state' && c.valueType === 'binary')
      || dedupedControls.find(c => c.valueType === 'binary');
    if (pw) heroConsumed.add(pw.property);
  } else if (uiModel.kind === 'curtain') {
    heroConsumed.add('state');
    heroConsumed.add('position');
  } else if (uiModel.kind === 'lock') {
    heroConsumed.add('lock_state');
  }
  const secondaryControls = dedupedControls.filter(c => !heroConsumed.has(c.property));

  const getExceptionBanner = () => {
    // Lock warning: unlocked
    if (uiModel.kind === 'lock') {
      const isLocked = currentDevice.state === 'LOCKED' || currentDevice.isLocked;
      if (!isLocked) {
        return (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-card border border-destructive/30 text-foreground">
            <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
            <span>{currentDevice.name || 'Device'} is unlocked</span>
          </div>
        );
      }
    }

    // Offline / not responding
    const isOffline = currentDevice.availabilityStatus === 'OFFLINE' || currentDevice.isConnected === false;
    if (isOffline || !uiModel.isAvailable) {
      return (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-card border border-yellow-500/30 text-foreground">
          <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0" />
          <span>{isOffline ? 'Offline' : 'No Response'}</span>
        </div>
      );
    }

    // Battery warning
    const battery = currentDevice.batteryLevel !== undefined ? currentDevice.batteryLevel : currentDevice.battery;
    if (battery !== undefined && battery < 20) {
      return (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-card border border-yellow-500/30 text-foreground">
          <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0" />
          <span>Battery low: {battery}%</span>
        </div>
      );
    }

    return null;
  };

  const getStateText = () => {
    if (uiModel.kind === 'lock') {
      const isLocked = currentDevice.state === 'LOCKED' || currentDevice.isLocked;
      const status = isLocked ? 'Locked' : 'Unlocked';
      const timeText = currentDevice.lastUpdated
        ? ` • ${new Date(currentDevice.lastUpdated).toLocaleString()}`
        : '';
      return `${status}${timeText}`;
    }

    if (currentDevice.lastUpdated) {
      return `Updated ${new Date(currentDevice.lastUpdated).toLocaleString()}`;
    }

    return 'Ready';
  };

  const getPrimaryControl = () => {
    if (!currentDevice.controls || currentDevice.controls.length === 0) return null;

    const primaryControl = currentDevice.controls[0];

    // Thermostat: setpoint is the hero (with power below), not the power
    // switch — adjusting the target is the main action.
    if (uiModel.kind === 'thermostat') {
      const setpoint = currentDevice.controls.find(c =>
        c.property?.includes('current_heating_setpoint')
      );

      if (setpoint) {
        const optimisticTarget = optimisticControlValues[setpoint.property];
        const target = optimisticTarget !== undefined ? optimisticTarget : setpoint.value;
        // Range lives under options.{min,max,step}; value_* is a legacy fallback.
        const step = setpoint.options?.step ?? setpoint.value_step ?? 1;
        const min = setpoint.options?.min ?? setpoint.value_min ?? 5;
        const max = setpoint.options?.max ?? setpoint.value_max ?? 35;

        // Current room temperature reads from sensors (local_temperature).
        const currentTemp = currentDevice.sensors?.find(s =>
          s.property?.includes('local_temperature') ||
          s.property?.includes('device_temperature')
        )?.value;
        const hasCurrent = currentTemp !== null && currentTemp !== undefined;

        const adjustTarget = (direction) => {
          const next = Math.max(min, Math.min(max, target + direction * step));
          handleControlChange(setpoint.property, next);
        };

        const power = currentDevice.controls.find(c => c.property === 'state' && c.valueType === 'binary')
          || currentDevice.controls.find(c => c.valueType === 'binary');
        const optimisticPower = power ? optimisticControlValues[power.property] : undefined;
        const powerValue = optimisticPower !== undefined ? optimisticPower : power?.value;
        const powerIsOn = power
          ? powerValue === true || powerValue === 'ON' || powerValue === power.options?.on
          : false;

        return (
          <div className="py-4 space-y-4">
            {hasCurrent && (
              <div className="text-center">
                <div className="text-4xl font-semibold">{Math.round(currentTemp)}°</div>
                <div className="text-xs text-muted-foreground mt-1">Current</div>
              </div>
            )}
            <div className="flex items-center justify-center gap-5">
              <button
                type="button"
                aria-label="Decrease target temperature"
                disabled={loading}
                onClick={() => adjustTarget(-1)}
                className="w-11 h-11 rounded-full border border-border bg-card shadow-sm flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
              >
                <Minus className="h-5 w-5" />
              </button>
              <div className="text-center min-w-[5rem]">
                <div className={cn(hasCurrent ? "text-2xl" : "text-4xl", "font-semibold")}>{Math.round(target)}°</div>
                <div className="text-xs text-muted-foreground mt-1">Target</div>
              </div>
              <button
                type="button"
                aria-label="Increase target temperature"
                disabled={loading}
                onClick={() => adjustTarget(1)}
                className="w-11 h-11 rounded-full border border-border bg-card shadow-sm flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {power && (
              <div className="flex items-center justify-center">
                <Switch
                  checked={powerIsOn}
                  onCheckedChange={(value) =>
                    handleControlChange(
                      power.property,
                      value ? (power.options?.on ?? 'ON') : (power.options?.off ?? 'OFF')
                    )
                  }
                  disabled={loading}
                />
              </div>
            )}
          </div>
        );
      }
    }

    // Binary (lights/switches): the big icon-circle hero handles this.
    if (primaryControl.valueType === 'binary') {
      if (heroBinaryControl) return null;
      const actualValue = primaryControl.value;
      const optimisticValue = optimisticControlValues[primaryControl.property];
      const isOn = optimisticValue !== undefined
        ? optimisticValue === true ||
          optimisticValue === 'ON' ||
          optimisticValue === primaryControl.options?.on
        : actualValue === true ||
          actualValue === 'ON' ||
          actualValue === primaryControl.options?.on;

      const nextOnValue = primaryControl.options?.on ?? 'ON';
      const nextOffValue = primaryControl.options?.off ?? 'OFF';

      return (
        <div className="flex items-center justify-center py-6">
          <Switch
            checked={isOn}
            onCheckedChange={(value) =>
              handleControlChange(
                primaryControl.property,
                value ? nextOnValue : nextOffValue
              )
            }
            disabled={loading}
            className="scale-150 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
          />
        </div>
      );
    }

    // Lock hero button
    if (uiModel.kind === 'lock') {
      const isLocked = currentDevice.state === 'LOCKED' || currentDevice.isLocked;
      return (
        <div className="flex items-center justify-center py-4">
          <button
            onClick={() => handleControlChange('state', isLocked ? 'UNLOCKED' : 'LOCKED')}
            disabled={loading}
            className="p-6 rounded-full bg-card border-2 border-border shadow-lg transition-colors active:bg-secondary"
          >
            {isLocked ? (
              <Lock className="h-12 w-12 text-foreground" />
            ) : (
              <Unlock className="h-12 w-12 text-foreground" />
            )}
          </button>
        </div>
      );
    }

    if (uiModel.kind === 'curtain') {
      const curtainStateControl = currentDevice.controls?.find(c =>
        c.property === 'state' &&
        c.type === 'enum' &&
        Array.isArray(c.options?.values)
      );
      const positionControl = currentDevice.controls?.find(c => c.property === 'position');

      const position = positionControl?.value;
      const isMoving = optimisticControlValues['state'] === 'MOVING' || curtainStateControl?.value === 'MOVING';

      let stateText = '';

      if (position !== null && position !== undefined) {
        if (position >= 98) {
          stateText = 'Open';
        } else if (position <= 2) {
          stateText = 'Closed';
        } else {
          stateText = `Open ${Math.round(position)}%`;
        }
      } else if (curtainStateControl?.value) {
        const value = curtainStateControl.value;
        if (value === 'OPEN') stateText = 'Open';
        else if (value === 'CLOSE') stateText = 'Closed';
        else if (value === 'STOP') stateText = 'Stopped';
        else stateText = value;
      }

      return (
        <div className="space-y-4">
          <div className="text-center py-2">
            <div className="text-2xl font-semibold">{stateText}</div>
            {position !== null && position !== undefined && (
              <div className="text-sm text-muted-foreground mt-1">{Math.round(position)}%</div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleControlChange('state', 'OPEN')}
              disabled={loading || isMoving}
              className="flex-1 h-11"
            >
              Open
            </Button>
            <Button
              variant="outline"
              onClick={() => handleControlChange('state', 'STOP')}
              disabled={loading || isMoving}
              className="flex-1 h-11"
            >
              Stop
            </Button>
            <Button
              variant="outline"
              onClick={() => handleControlChange('state', 'CLOSE')}
              disabled={loading || isMoving}
              className="flex-1 h-11"
            >
              Close
            </Button>
          </div>

          {isMoving && (
            <div className="text-center text-sm text-muted-foreground">
              Moving...
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const renderControlRow = (control) => {
    if (control.valueType === 'binary') {
      const actualValue = control.value;
      const optimisticValue = optimisticControlValues[control.property];
      const isOn = optimisticValue !== undefined
        ? optimisticValue === true || optimisticValue === 'ON' || optimisticValue === control.options?.on
        : actualValue === true || actualValue === 'ON' || actualValue === control.options?.on;

      return (
        <div key={control.property} className="flex items-center justify-between py-3 px-4 border-b border-border/10 last:border-0">
          <span className="text-sm font-medium text-foreground">{control.name || control.property}</span>
          <Switch
            checked={isOn}
            onCheckedChange={(value) =>
              handleControlChange(control.property, value ? (control.options?.on ?? true) : (control.options?.off ?? false))
            }
            disabled={loading}
          />
        </div>
      );
    }

    if (control.type === 'enum' && control.options?.values) {
      const selectedValue = control.value;

      return (
        <div key={control.property} className="px-4 py-3 border-b border-border/10 last:border-0">
          <div className="text-sm font-medium text-foreground mb-2">{control.name || control.property}</div>
          <div className="flex flex-wrap gap-2">
            {control.options.values.map((value) => (
              <Button
                key={value}
                variant={selectedValue === value ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleControlChange(control.property, value)}
                disabled={loading}
                className="text-xs"
              >
                {value}
              </Button>
            ))}
          </div>
        </div>
      );
    }

    if (control.valueType === 'numeric' || typeof control.value === 'number') {
      // Range/unit live under options; value_* / unit are legacy fallbacks.
      const min = control.options?.min ?? control.value_min ?? 0;
      const max = control.options?.max ?? control.value_max ?? 100;
      const step = control.options?.step ?? control.value_step ?? 1;
      const unit = control.unit ?? control.options?.unit ?? '';

      return (
        <div key={control.property} className="px-4 py-3 border-b border-border/10 last:border-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">{control.name || control.property}</span>
            <span className="text-sm font-medium text-foreground">
              {control.value}
              {unit ? ` ${unit}` : ''}
            </span>
          </div>

          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={control.value}
            onChange={(e) => handleControlChange(control.property, Number(e.target.value))}
            disabled={loading}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
          />

          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>
              {min}
              {unit}
            </span>
            <span>
              {max}
              {unit}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div key={control.property} className="flex items-center justify-between py-3 px-4 border-b border-border/10 last:border-0">
        <div>
          <div className="text-sm font-medium text-foreground">{control.name || control.property}</div>
          <div className="text-xs text-muted-foreground">{control.type || 'unknown'} type</div>
        </div>
        <div className="text-sm font-medium text-foreground">{String(control.value)}</div>
      </div>
    );
  };

  // Binary sensors arrive as JS booleans; React renders `false`/`true` as
  // nothing, so presence / PIR rows showed an empty value. Map them to words.
  const BINARY_SENSOR_LABELS = {
    presence: ['Present', 'Absent'],
    occupancy: ['Occupied', 'Clear'],
    pir_detection: ['Motion', 'No motion'],
    contact: ['Closed', 'Open'],
    water_leak: ['Leak', 'Dry'],
    vibration: ['Vibrating', 'Still'],
    tamper: ['Tampered', 'OK'],
  };

  const formatSensorValue = (sensor) => {
    const { value, unit, property } = sensor;
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean' || value === 'ON' || value === 'OFF') {
      const on = value === true || value === 'ON';
      const [onLabel, offLabel] = BINARY_SENSOR_LABELS[property] || ['On', 'Off'];
      return on ? onLabel : offLabel;
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return `${value}${unit ? ` ${unit}` : ''}`;
  };

  const renderSensorRow = (sensor) => (
    <div key={sensor.property} className="flex items-center justify-between py-3 px-4 border-b border-border/10 last:border-0">
      <div>
        <div className="text-sm font-medium text-foreground">{sensor.name || sensor.property}</div>
        <div className="text-xs text-muted-foreground">{sensor.type || 'sensor'}</div>
      </div>
      <div className="text-sm font-medium text-foreground">
        {formatSensorValue(sensor)}
      </div>
    </div>
  );

  // Big centered icon-circle hero, mirroring the mobile app. For lights/switches
  // it IS the control (tap to toggle, "On/Off" below); for other types it's a
  // calm type indicator above their dedicated controls. Locks keep their own
  // hero button, so skip it there.
  const renderHeroCircle = () => {
    if (uiModel.kind === 'lock') return null;

    if (heroBinaryControl) {
      const optimistic = optimisticControlValues[heroBinaryControl.property];
      const isOn = optimistic !== undefined
        ? (optimistic === true || optimistic === 'ON' || optimistic === heroBinaryControl.options?.on)
        : (heroBinaryControl.value === true || heroBinaryControl.value === 'ON' || heroBinaryControl.value === heroBinaryControl.options?.on);
      const nextOn = heroBinaryControl.options?.on ?? 'ON';
      const nextOff = heroBinaryControl.options?.off ?? 'OFF';

      return (
        <div className="flex flex-col items-center py-4">
          <button
            type="button"
            aria-pressed={isOn}
            aria-label={`Turn ${isOn ? 'off' : 'on'} ${deviceTitle}`}
            disabled={loading}
            onClick={() => handleControlChange(heroBinaryControl.property, isOn ? nextOff : nextOn)}
            className="relative rounded-full transition-transform active:scale-95 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {isOn && <span className="absolute -inset-3 rounded-full bg-amber-300/25 blur-xl" />}
            <span className={cn(
              "relative flex items-center justify-center w-28 h-28 rounded-full border-2 transition-colors",
              isOn
                ? "bg-amber-100 text-amber-600 border-amber-200 dark:bg-amber-400/15 dark:text-amber-400 dark:border-amber-400/30 shadow-[0_10px_30px_rgba(245,158,11,0.25)]"
                : "bg-muted text-muted-foreground border-border"
            )}>
              <IconComponent className="w-12 h-12" />
            </span>
          </button>
          <p className="mt-4 text-lg font-semibold text-foreground">{isOn ? 'On' : 'Off'}</p>
        </div>
      );
    }

    // Decorative type indicator (thermostat / curtain / sensor / other).
    const active = uiModel.isOn;
    return (
      <div className="flex justify-center pt-4 pb-1">
        <span className={cn(
          "flex items-center justify-center w-20 h-20 rounded-full border-2",
          active
            ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-500/15 dark:text-green-400 dark:border-green-400/30"
            : "bg-primary/10 text-primary border-primary/15"
        )}>
          <IconComponent className="w-9 h-9" />
        </span>
      </div>
    );
  };

  const renderControlsSection = () => {
    const displayedControls = showAllControls ? secondaryControls : secondaryControls.slice(0, 3);

    return (
      <>
        {displayedControls.map(renderControlRow)}

        {secondaryControls.length > 3 && !showAllControls && (
          <button
            onClick={() => setShowAllControls(true)}
            className="flex items-center gap-2 w-full py-3 px-4 text-sm text-primary hover:bg-secondary/50 transition-colors"
          >
            <ChevronDown className="h-4 w-4" />
            <span>Show {secondaryControls.length - 3} more controls</span>
          </button>
        )}
      </>
    );
  };

  const renderSensorsSection = () => {
    if (!currentDevice.sensors || currentDevice.sensors.length === 0) return null;
    return <>{currentDevice.sensors.map(renderSensorRow)}</>;
  };

  const exceptionBanner = getExceptionBanner();

  return (
    <ResponsiveSheet open={open} onClose={onClose} size="lg">
        <DialogHeader className="shrink-0 space-y-1 px-10 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-lg font-bold text-foreground text-center truncate">{deviceTitle}</DialogTitle>
          <div className="flex items-center justify-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground truncate">{locationText || 'No location'}</p>
          </div>
        </DialogHeader>

        <Tabs defaultValue="controls" className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-4 shrink-0">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="controls">Controls</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="controls" className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4 mt-0">
            {exceptionBanner}

            {renderHeroCircle()}

            {getPrimaryControl()}

            <div className="text-center text-xs text-muted-foreground">{getStateText()}</div>

            {/* Controls group — only when there are controls beyond the hero */}
            {secondaryControls.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-3 bg-secondary text-sm font-medium text-foreground border-b border-border">
                  Controls
                </div>
                {renderControlsSection()}
              </div>
            )}

            {/* Sensors group */}
            {currentDevice.sensors && currentDevice.sensors.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-3 bg-secondary text-sm font-medium text-foreground border-b border-border">
                  Status
                </div>
                {renderSensorsSection()}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="flex-1 min-h-0 overflow-y-auto px-6 py-4 mt-0">
            <div className="space-y-4 pb-20">
              <DeviceEditor deviceId={deviceId} devices={devices} onClose={onClose} />

              <div className="border-t border-border pt-4">
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                  className="w-full py-2.5 px-4 text-sm text-destructive border border-destructive/30 rounded-lg hover:bg-destructive/5 transition-colors"
                >
                  Remove Accessory
                </button>
              </div>

              <details className="border-t border-border pt-4">
                <summary className="flex items-center gap-2 w-full py-2 px-4 text-sm font-medium text-muted-foreground hover:bg-secondary/50 transition-colors cursor-pointer">
                  <ChevronRight className="h-4 w-4" />
                  Debug Information
                </summary>
                <div className="pb-2">
                  <div className="px-4 py-2 text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">
                    Debug Information
                  </div>

                  <div className="border border-border/20 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between py-3 px-4 bg-card border-b border-border/10">
                      <span className="text-xs text-muted-foreground">Device ID</span>
                      <code className="bg-muted px-2 py-0.5 rounded font-mono text-xs">{currentDevice.id}</code>
                    </div>

                    {currentDevice.parentId && (
                      <div className="flex items-center justify-between py-3 px-4 bg-card border-b border-border/10">
                        <span className="text-xs text-muted-foreground">Parent ID</span>
                        <code className="bg-muted px-2 py-0.5 rounded font-mono text-xs">{currentDevice.parentId}</code>
                      </div>
                    )}

                    {currentDevice.linkquality !== undefined && currentDevice.linkquality !== null && (
                      <div className="flex items-center justify-between py-3 px-4 bg-card border-b border-border/10">
                        <span className="text-xs text-muted-foreground">Link Quality</span>
                        <span className="text-sm font-medium text-foreground">{currentDevice.linkquality}</span>
                      </div>
                    )}

                    {currentDevice.lastUpdated && (
                      <div className="flex items-center justify-between py-3 px-4 bg-card border-b border-border/10">
                        <span className="text-xs text-muted-foreground">Last Updated</span>
                        <span className="text-sm font-medium text-foreground">{new Date(currentDevice.lastUpdated).toLocaleString()}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between py-3 px-4 bg-card border-b border-border/10">
                      <span className="text-xs text-muted-foreground">Type</span>
                      <span className="text-sm font-medium text-foreground">{uiModel.kind}</span>
                    </div>

                    <div className="flex items-center justify-between py-3 px-4 bg-card">
                      <span className="text-xs text-muted-foreground">Location</span>
                      <span className="text-sm font-medium text-foreground">{uiModel.fullLocation || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
              </details>
            </div>
          </TabsContent>
        </Tabs>

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="max-w-sm w-full bg-card border border-border shadow-2xl">
              <CardHeader>
                <CardTitle className="text-lg text-foreground">Delete Device</CardTitle>
              </CardHeader>

              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to delete "{currentDevice.name || currentDevice.friendly_name || 'this device'}"?
                  {currentDevice.parentId || currentDevice.id.includes('_') ? (
                    <span className="block mt-2 text-sm text-orange-600">
                      ⚠️ This will remove the entire physical device and all its gangs/switches from the Zigbee network.
                    </span>
                  ) : null}
                  <span className="block mt-1 text-sm">This action cannot be undone.</span>
                </p>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteConfirm}
                    disabled={loading}
                  >
                    {loading ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
    </ResponsiveSheet>
  );
};

export default DeviceDetailsSheet;
